'use client';

import { fetchGoodsList } from '@/api/product';
import type { IProductListApiParams, IProductListApiResponse } from '@/api/types/product';
import { useClientOnly } from '@/hooks/useClientOnly';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { DEFAULT_SORT_VALUE } from '@/utils/constants';
import { transformFilterParams } from '@/utils/goods-list-utils';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import useSWR from 'swr';
import GoodsListLoadingFallback from '../components/GoodsListLoadingFallback';
import GoodsListSeoFooter from '../components/GoodsListSeoFooter';
import { toGoodsListInitialData } from '../utils/goods-list-client-dto';
import GoodsListPageClient from './page.client';

const LIMIT = 36;

export default function GoodsListCsrPage() {
  const isClient = useClientOnly();
  const searchParams = useSearchParams();
  const query = searchParams.toString();
  const currency = useStore((state) => state.currency.name);
  const token = useStore((state) => state.token);
  const t = useTranslations('goodsList.seo');
  const errorT = useTranslations('error');
  const { data, error, mutate } = useSWR(
    isClient ? ['goods-list-81', currency, token, query] : null,
    async ([, currentCurrency, currentToken, currentQuery]) => {
      const params = new URLSearchParams(currentQuery);
      const page = Math.max(1, Number.parseInt(params.get('page') || '1', 10) || 1);
      let filter = {};
      try {
        filter = JSON.parse(params.get('filter') || '{}');
      } catch {
        // 无效筛选回退为空。
      }
      const sort = params.get('sort') || DEFAULT_SORT_VALUE;
      const adp = params.get('adp');
      const request: IProductListApiParams = {
        currency: currentCurrency,
        group_path: '81',
        limit: LIMIT,
        page,
        order: sort === DEFAULT_SORT_VALUE ? '' : sort,
        in_stock: 1,
        is_sale: 1,
        filter: transformFilterParams(filter),
        append_correlation: 1,
        show_adv: 1,
        top_shop_skus: page === 1 && adp ? adp.split(',').slice(0, 6) : undefined,
      };
      const response = (await fetchGoodsList(request)) as unknown as {
        code: number;
        data?: IProductListApiResponse;
      };
      if (response.code !== 200 || !response.data?.group || !response.data.list) {
        throw new Error('Failed to load goods-list group 81');
      }
      return {
        currency: currentCurrency,
        token: currentToken,
        query: currentQuery,
        result: response.data,
      };
    },
    { keepPreviousData: true },
  );

  const displayedData = data?.currency === currency && data.token === token ? data : undefined;
  const displayedSearchParams = useMemo(
    () => new URLSearchParams(displayedData?.query),
    [displayedData?.query],
  );
  const page = Math.max(1, Number.parseInt(displayedSearchParams.get('page') || '1', 10) || 1);
  const initialData = useMemo(
    () => (displayedData ? toGoodsListInitialData(displayedData.result, page, LIMIT) : undefined),
    [displayedData, page],
  );
  if (!isClient) return null;
  const errorAlert = error ? (
    <div
      role="alert"
      className="mx-auto flex min-h-[320px] flex-col items-center justify-center gap-16"
    >
      <p>{errorT('description')}</p>
      <button type="button" onClick={() => void mutate()} className="rounded-8 border px-24 py-12">
        {errorT('tryAgain')}
      </button>
    </div>
  ) : null;
  if (!displayedData || !initialData) {
    if (errorAlert) return errorAlert;
    return <GoodsListLoadingFallback />;
  }

  return (
    <>
      {errorAlert}
      <GoodsListPageClient
        key={`${currency}:${token}`}
        initialData={initialData}
        searchParams={{
          sort: displayedSearchParams.get('sort') || undefined,
          filter: displayedSearchParams.get('filter') || undefined,
          adp: displayedSearchParams.get('adp') || undefined,
          page: displayedSearchParams.get('page') || undefined,
        }}
        isCsrDataPending={displayedData.query !== query}
        seoFaqRendering="external"
        seoFooter={
          <GoodsListSeoFooter
            groupInfo={displayedData.result.group}
            labels={{
              next: t('nextSeoContent'),
              previous: t('previousSeoContent'),
              slide: t('showSeoContent', { index: '{index}' }),
            }}
          />
        }
      />
    </>
  );
}
